import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UploadDocumentsModal } from "../../contacts/components/UploadDocumentsModal";
import { CreateAdditionalWorksInvoiceModal, } from "./CreateAdditionalWorksInvoiceModal";
import { additionalWorksService, } from "@/api/services/additional-works.service";
import { jobsService } from "@/api/services/jobs.service";
import { documentsService, } from "@/api/services/documents.service";
import { xeroService } from "@/api/services/xero.service";
import { DateInput, useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { toDateOnly, getTimeFromIso, toDateTimeLocal } from "@/utils/dateTime";
import styles from "./JobAdditionalWorksTab.module.css";
const FileIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" }), _jsx("polyline", { points: "13 2 13 9 20 9" })] }));
const DownloadIcon = () => (_jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), _jsx("polyline", { points: "7 10 12 15 17 10" }), _jsx("line", { x1: "12", y1: "15", x2: "12", y2: "3" })] }));
const JOB_DOCUMENT_CATEGORIES = [
    "Permits",
    "Plans",
    "Invoices",
    "Images",
    "Additional Works",
    "Other",
];
/** Prefix in document description to associate with a specific additional work. */
const ADDITIONAL_WORK_DOC_PREFIX = "[additionalWork:";
const docBelongsToWork = (doc, workId) => (doc.description ?? "").includes(`${ADDITIONAL_WORK_DOC_PREFIX}${workId}]`);
const isUnassignedDoc = (doc) => !(doc.description ?? "").includes(ADDITIONAL_WORK_DOC_PREFIX);
/** Parse user-visible label from document description (strips the additionalWork prefix). */
const getDocumentDisplayLabel = (doc) => (doc.description ?? "")
    .replace(/^\[additionalWork:[^\]]+\]\s*/, "")
    .trim() || "Document";
export const JobAdditionalWorksTab = ({ jobId, job, refetch, refetchJobDocumentsFromDocumentsApi, refetchJobDocuments, }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const [additionalWorks, setAdditionalWorks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [addDocumentDefaultCategory, setAddDocumentDefaultCategory] = useState("Additional Works");
    const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
    const [createInvoiceForWork, setCreateInvoiceForWork] = useState(null);
    const [addDocumentsForWork, setAddDocumentsForWork] = useState(null);
    const [savingId, setSavingId] = useState(null);
    const [statusSavingId, setStatusSavingId] = useState(null);
    const pendingUploadDescriptionRef = useRef(undefined);
    const { user } = useAuth();
    const basePath = useMemo(() => (location.pathname.startsWith("/staff") ? "/staff" : "/admin"), [location.pathname]);
    const fetchAdditionalWorks = useCallback(async () => {
        if (!jobId) {
            setAdditionalWorks([]);
            return;
        }
        setLoading(true);
        try {
            const res = await additionalWorksService.getAdditionalWorksByJob(jobId);
            const list = res.data?.additionalWorks;
            setAdditionalWorks(Array.isArray(list) ? list : []);
        }
        catch (err) {
            console.error("Failed to fetch additional works:", err);
            setAdditionalWorks([]);
            toast.error("Failed to load additional works.");
        }
        finally {
            setLoading(false);
        }
    }, [jobId, toast]);
    const fetchDocuments = useCallback(async () => {
        if (!jobId) {
            setDocuments([]);
            return;
        }
        try {
            const res = await documentsService.getDocumentsByJob(jobId, "additional_works");
            setDocuments(Array.isArray(res.data) ? res.data : []);
        }
        catch (err) {
            console.error("Failed to fetch job documents:", err);
            setDocuments([]);
        }
    }, [jobId]);
    useEffect(() => {
        fetchAdditionalWorks();
    }, [fetchAdditionalWorks]);
    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);
    const handleCreateAdditionalWork = () => {
        const params = new URLSearchParams();
        if (jobId)
            params.set("mainJobId", jobId);
        if (job?.siteId)
            params.set("siteId", job.siteId);
        navigate(`${basePath}/estimating/create-additional?${params.toString()}`);
    };
    const toggleAccordion = (id) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };
    const handleAddDocumentsForWork = (work) => {
        setAddDocumentsForWork(work);
        setAddDocumentDefaultCategory("Additional Works");
        pendingUploadDescriptionRef.current = `${ADDITIONAL_WORK_DOC_PREFIX}${work.id}]`;
        setIsUploadModalOpen(true);
    };
    const handleSaveDocuments = async (uploadedFiles, category) => {
        if (!jobId) {
            toast.warning("Job not found.");
            setIsUploadModalOpen(false);
            return;
        }
        const key = (category ?? "").trim().toLowerCase().replace(/\s+/g, "_");
        const jobCategory = key === "additional_works"
            ? "additional_works"
            : key === "permits"
                ? "permits"
                : key === "plans"
                    ? "plans"
                    : key === "invoices"
                        ? "invoices"
                        : key === "images"
                            ? "images"
                            : "other";
        const description = addDocumentsForWork
            ? pendingUploadDescriptionRef.current
            : undefined;
        try {
            for (const f of uploadedFiles) {
                if (!f.file)
                    throw new Error(`File missing for ${f.name}`);
                await documentsService.uploadJobDocument(f.file, {
                    jobId,
                    name: f.name,
                    jobCategory: jobCategory,
                    ...(description && { description }),
                });
            }
            pendingUploadDescriptionRef.current = undefined;
            setAddDocumentsForWork(null);
            await fetchDocuments();
            await refetchJobDocumentsFromDocumentsApi?.();
            await refetchJobDocuments?.();
            setIsUploadModalOpen(false);
            setAddDocumentDefaultCategory(null);
            toast.success("Documents uploaded.");
        }
        catch (err) {
            console.error("Failed to upload documents:", err);
            toast.error("Failed to upload documents.");
        }
    };
    const handleDownloadDoc = async (doc) => {
        try {
            const res = await documentsService.getDownloadUrl(doc.id);
            window.open(res.data.url, "_blank");
        }
        catch (err) {
            console.error("Failed to get download URL:", err);
            toast.error("Failed to download.");
        }
    };
    const handleDeleteDoc = async (doc) => {
        if (!window.confirm("Delete this document?"))
            return;
        try {
            await documentsService.deleteDocument(doc.id);
            await fetchDocuments();
            toast.success("Document deleted.");
        }
        catch (err) {
            console.error("Failed to delete document:", err);
            toast.error("Failed to delete document.");
        }
    };
    const handleViewQuote = (work) => {
        const quoteId = work.quoteId;
        if (!quoteId) {
            toast.warning("No quote linked to this additional work.");
            return;
        }
        const url = `${window.location.origin}${basePath}/estimating/${quoteId}/view`;
        window.open(url, "_blank");
    };
    const handleOpenCreateInvoice = (work) => {
        setCreateInvoiceForWork(work);
        setCreateInvoiceModalOpen(true);
    };
    const handleCloseCreateInvoice = () => {
        setCreateInvoiceModalOpen(false);
        setCreateInvoiceForWork(null);
    };
    const handleSaveCreateInvoice = async (payload) => {
        const contactId = job?.contactId;
        if (!contactId) {
            toast.warning("No contact is linked to this job. Cannot create invoice.");
            return;
        }
        if (!createInvoiceForWork) {
            toast.warning("Additional work not found.");
            return;
        }
        try {
            await xeroService.createInvoice({
                contactId,
                amount: payload.invoiceAmount,
                invoiceDescription: payload.description || "",
                jobId: createInvoiceForWork.id,
                scopeItems: payload.scopeItems && payload.scopeItems.length > 0
                    ? payload.scopeItems
                    : undefined,
            });
            if (jobId) {
                await jobsService.updateJobStatus(jobId, "invoiced");
                await refetch();
            }
            toast.success("Invoice created successfully.");
            handleCloseCreateInvoice();
        }
        catch (err) {
            console.error("Failed to create invoice:", err);
            const message = err && typeof err === "object" && "response" in err
                ? err.response
                    ?.data?.message
                : null;
            toast.error(message
                ? `Failed to create invoice: ${message}`
                : "Failed to create invoice. Please try again.");
        }
    };
    const handleSaveWork = useCallback(async (work, patch) => {
        setSavingId(work.id);
        try {
            await additionalWorksService.updateAdditionalWork(work.id, patch);
            await fetchAdditionalWorks();
            toast.success("Saved.");
        }
        catch (err) {
            console.error("Failed to save additional work:", err);
            toast.error("Failed to save.");
        }
        finally {
            setSavingId(null);
        }
    }, [fetchAdditionalWorks, toast]);
    const handleStatusChange = useCallback(async (work, newStatus) => {
        setStatusSavingId(work.id);
        try {
            await additionalWorksService.updateAdditionalWorkStatus(work.id, {
                status: newStatus,
                ...(newStatus === "close" && {
                    approvedById: user?.id,
                    approvedByName: user?.name ?? undefined,
                }),
            });
            await fetchAdditionalWorks();
            toast.success(newStatus === "close" ? "Additional work closed." : "Additional work reopened.");
        }
        catch (err) {
            console.error("Failed to update additional work status:", err);
            toast.error("Failed to update status.");
        }
        finally {
            setStatusSavingId(null);
        }
    }, [fetchAdditionalWorks, toast, user?.id, user?.name]);
    if (!jobId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "No job selected." }) }));
    }
    if (loading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loadingState, children: "Loading additional works..." }) }));
    }
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.header, children: [_jsx("h2", { className: styles.sectionTitle, children: "Additional Works" }), _jsxs("button", { type: "button", className: styles.addInvoiceBtn, onClick: handleCreateAdditionalWork, children: [_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "12", y1: "5", x2: "12", y2: "19" }), _jsx("line", { x1: "5", y1: "12", x2: "19", y2: "12" })] }), "Additional Works"] })] }), _jsx("div", { className: styles.accordionList, children: additionalWorks.length === 0 ? (_jsx("div", { className: styles.accordionEmpty, children: "No additional works. Create one to get started." })) : (_jsxs(_Fragment, { children: [(() => {
                            const unassignedDocs = documents.filter(isUnassignedDoc);
                            return unassignedDocs.length > 0 ? (_jsxs("div", { className: styles.unassignedSection, children: [_jsx("h3", { className: styles.unassignedTitle, children: "Unassigned documents" }), _jsx("p", { className: styles.unassignedHint, children: "These documents were uploaded before work-specific scoping. They are not linked to any additional work." }), _jsx("ul", { className: styles.documentList, children: unassignedDocs.map((doc) => (_jsxs("li", { className: styles.documentRow, children: [_jsx("span", { className: styles.documentLabel, children: getDocumentDisplayLabel(doc) }), _jsxs("div", { className: styles.documentActions, children: [_jsxs("div", { className: styles.documentFile, children: [_jsx(FileIcon, {}), _jsx("span", { children: doc.name || doc.fileName })] }), _jsx("button", { type: "button", className: styles.downloadBtn, onClick: () => handleDownloadDoc(doc), title: "Download", "aria-label": "Download document", children: _jsx(DownloadIcon, {}) }), _jsx("button", { type: "button", className: styles.removeBtn, onClick: () => handleDeleteDoc(doc), title: "Remove", "aria-label": "Remove document", children: "\u2715" })] })] }, doc.id))) })] })) : null;
                        })(), additionalWorks.map((work) => (_jsx(AdditionalWorkAccordion, { work: work, documents: documents.filter((d) => docBelongsToWork(d, work.id)), isExpanded: expandedId === work.id, onToggle: () => toggleAccordion(work.id), onAddDocuments: () => handleAddDocumentsForWork(work), onDownloadDoc: handleDownloadDoc, onDeleteDoc: handleDeleteDoc, onViewQuote: () => handleViewQuote(work), onOpenCreateInvoice: () => handleOpenCreateInvoice(work), onSave: handleSaveWork, onStatusChange: handleStatusChange, saving: savingId === work.id, statusSaving: statusSavingId === work.id, basePath: basePath, getDocumentDisplayLabel: getDocumentDisplayLabel }, work.id)))] })) }), _jsx(UploadDocumentsModal, { isOpen: isUploadModalOpen, onClose: () => {
                    setIsUploadModalOpen(false);
                    setAddDocumentDefaultCategory(null);
                    setAddDocumentsForWork(null);
                    pendingUploadDescriptionRef.current = undefined;
                }, onSave: handleSaveDocuments, categories: JOB_DOCUMENT_CATEGORIES, defaultCategory: addDocumentDefaultCategory ?? undefined }), _jsx(CreateAdditionalWorksInvoiceModal, { isOpen: createInvoiceModalOpen, onClose: handleCloseCreateInvoice, onSave: handleSaveCreateInvoice, quoteId: createInvoiceForWork?.quoteId ?? undefined, fallbackAmount: createInvoiceForWork?.estimatedCost ?? undefined, fallbackDescription: createInvoiceForWork?.title != null
                    ? createInvoiceForWork.title
                    : undefined, jobNumber: createInvoiceForWork?.jobNumber ?? undefined })] }));
};
const isWorkClosed = (work) => work.status === "close";
function AdditionalWorkAccordion({ work, documents, isExpanded, onToggle, onAddDocuments, onDownloadDoc, onDeleteDoc, onViewQuote, onOpenCreateInvoice, onSave, onStatusChange, saving, statusSaving, getDocumentDisplayLabel, }) {
    const toast = useToast();
    const [duration, setDuration] = useState(work.duration != null ? String(work.duration) : "");
    const [commencementDate, setCommencementDate] = useState(() => toDateOnly(work.commencementDate));
    const [commencementTime, setCommencementTime] = useState(() => getTimeFromIso(work.commencementDate));
    const [completionDate, setCompletionDate] = useState(() => toDateOnly(work.completionDate));
    const [completionTime, setCompletionTime] = useState(() => getTimeFromIso(work.completionDate));
    const [variationDetails, setVariationDetails] = useState(work.description ?? "");
    const handleSave = () => {
        const patch = {
            duration: duration.trim() ? parseInt(duration, 10) : undefined,
            commencementDate: toDateTimeLocal(commencementDate, commencementTime) || undefined,
            completionDate: toDateTimeLocal(completionDate, completionTime) || undefined,
            description: variationDetails.trim() || undefined,
        };
        onSave(work, patch);
    };
    const closed = isWorkClosed(work);
    return (_jsxs("div", { className: styles.accordionItem, children: [_jsxs("div", { className: styles.accordionHeader, children: [_jsxs("button", { type: "button", className: styles.accordionToggle, onClick: onToggle, "aria-expanded": isExpanded, children: [_jsx("span", { className: styles.accordionTitle, children: work.jobNumber
                                    ? `Job #${work.jobNumber}`
                                    : work.title || "Additional work" }), _jsx("span", { className: styles.accordionChevron, children: isExpanded ? "▼" : "▶" })] }), _jsx("button", { type: "button", className: closed ? styles.statusBtnClosed : styles.statusBtnOpen, onClick: (e) => {
                            e.stopPropagation();
                            onStatusChange(work, closed ? "open" : "close");
                        }, disabled: statusSaving, title: closed ? "Reopen" : "Mark as closed", children: statusSaving ? "…" : closed ? "Closed" : "Open" })] }), isExpanded && (_jsxs("div", { className: styles.accordionBody, children: [_jsxs("div", { className: styles.cardContent, children: [_jsxs("div", { className: styles.leftSection, children: [_jsx("div", { className: styles.formGroup, children: _jsx("span", { className: styles.documentLabel, children: "Job Dates" }) }), _jsxs("div", { className: styles.formGroup, children: [_jsx("label", { className: styles.label, children: "Duration (days)" }), _jsx("input", { type: "number", className: styles.durationInput, min: 0, value: duration, onChange: (e) => setDuration(e.target.value) })] }), _jsxs("div", { className: styles.formGroup, children: [_jsx("label", { className: styles.label, children: "Commencement Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx(DateInput, { className: styles.input, value: commencementDate, onChange: setCommencementDate, onInvalid: () => toast.error("Please enter a valid date.") }), _jsx("input", { type: "time", className: styles.input, value: commencementTime, onChange: (e) => setCommencementTime(e.target.value) })] })] }), _jsxs("div", { className: styles.formGroup, children: [_jsx("label", { className: styles.label, children: "Completion Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx(DateInput, { className: styles.input, value: completionDate, onChange: setCompletionDate, onInvalid: () => toast.error("Please enter a valid date.") }), _jsx("input", { type: "time", className: styles.input, value: completionTime, onChange: (e) => setCompletionTime(e.target.value) })] })] }), _jsxs("div", { className: styles.formGroup, children: [_jsx("label", { className: styles.label, children: "Job Variation Details" }), _jsx("textarea", { className: styles.variationTextarea, value: variationDetails, onChange: (e) => setVariationDetails(e.target.value), placeholder: "Enter variation details...", rows: 4 })] })] }), _jsx("div", { className: styles.rightSection, children: _jsxs("div", { className: styles.documentsArea, children: [_jsx("h3", { className: styles.viewDocumentsSectionTitle, children: "View Documents" }), _jsx("button", { type: "button", className: styles.browseBtn, onClick: onAddDocuments, children: "Add Documents" }), documents.length === 0 ? (_jsx("p", { className: styles.accordionEmpty, children: "No documents" })) : (_jsx("ul", { className: styles.documentList, children: documents.map((doc) => (_jsxs("li", { className: styles.documentRow, children: [_jsx("span", { className: styles.documentLabel, children: getDocumentDisplayLabel(doc) }), _jsxs("div", { className: styles.documentActions, children: [_jsxs("div", { className: styles.documentFile, children: [_jsx(FileIcon, {}), _jsx("span", { children: doc.name || doc.fileName })] }), _jsx("button", { type: "button", className: styles.downloadBtn, onClick: () => onDownloadDoc(doc), title: "Download", "aria-label": "Download document", children: _jsx(DownloadIcon, {}) }), _jsx("button", { type: "button", className: styles.removeBtn, onClick: () => onDeleteDoc(doc), title: "Remove", "aria-label": "Remove document", children: "\u2715" })] })] }, doc.id))) }))] }) })] }), _jsxs("div", { className: styles.accordionFooter, children: [_jsx("button", { type: "button", className: styles.viewDetailsBtn, onClick: onViewQuote, disabled: !work.quoteId, children: "View Quote" }), _jsx("button", { type: "button", className: styles.viewDetailsBtn, onClick: onOpenCreateInvoice, children: "Create Invoice" }), _jsx("button", { type: "button", className: styles.saveBtn, onClick: handleSave, disabled: saving, children: saving ? "Saving…" : "Save" })] })] }))] }));
}
